import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory } from 'react-router-dom';
import { List, Map } from 'immutable';
import { Popover, PopoverPanel } from '@headlessui/react';
import { createAndShowChatSessionWithMessage } from 'flow/actions/chat-actions';
import BackButton from 'flow/components/ui-components/BackButton';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useAutoFocus from 'flow/hooks/useAutoFocus';
import useExtension from 'flow/hooks/useExtension';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { Capabilities } from 'flow/utils/capability-utils';
import { routes } from 'flow/types/routes';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { extractAbbreviatedName, extractName } from 'flow/utils';
import { filterByAllfileds, getExtensionHasCapability } from 'flow/utils/extension-utils-TW';
import { twMerge } from 'tailwind-merge';
import MessageInput from './MessageInput';
import MessageList from 'flow/components/chat/v2/MessageList';
import ActiveProfileStatus from 'flow/components/ui-components/ActiveProfileStatus';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var NewChatPage = function NewChatPage() {
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var history = useHistory();
  var _useModal = useModal(),
    openModal = _useModal.openModal;
  var _useSinglePaneMode = useSinglePaneMode(),
    isSinglePaneMode = _useSinglePaneMode.isSinglePaneMode;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    chosenExtensions = _useState2[0],
    setChosenExtensions = _useState2[1];
  var scrollViewRef = useRef(null);

  // Add useEffect to scroll to bottom when we add a new member
  useEffect(function () {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTop = scrollViewRef.current.scrollHeight;
    }
  }, [chosenExtensions]);
  return /*#__PURE__*/_jsxs("div", {
    className: "flex size-full flex-col bg-white",
    children: [/*#__PURE__*/_jsxs("header", {
      className: "flex h-fit w-full flex-col gap-x-1 gap-y-2 border-b px-6 py-2 sm:min-h-16 sm:flex-row sm:items-center sm:gap-x-4 sm:py-0",
      children: [isSinglePaneMode && /*#__PURE__*/_jsx("div", {
        className: "flex",
        children: /*#__PURE__*/_jsx(BackButton, {
          onClick: function onClick() {
            return history.push(routes['messages']);
          }
        })
      }), /*#__PURE__*/_jsx("p", {
        className: "text-base/5",
        children: "".concat(i18next.t('call.to'), ":")
      }), /*#__PURE__*/_jsx("div", {
        ref: scrollViewRef,
        className: "max-h-56 flex-1 overflow-y-scroll py-3",
        children: /*#__PURE__*/_jsx(ContactInput, {
          chosenExtensions: chosenExtensions,
          setChosenExtensions: setChosenExtensions
        })
      }), /*#__PURE__*/_jsx(Button, {
        className: "text-main max-w-[90px] text-sm/4 font-medium sm:min-w-[120px]",
        onClick: function onClick() {
          return openModal('manageRoom', {
            chosenExtensions: List(chosenExtensions),
            header: i18next.t('Chat.Create.Room'),
            confirmButtonText: i18next.t('Chat.Create.Room')
          });
        },
        disabled: chosenExtensions.length < 1,
        children: i18next.t('Chat.Create.Room')
      })]
    }), chosenExtensions.length > 0 && /*#__PURE__*/_jsx(NewChatView, {
      extensions: chosenExtensions
    })]
  });
};
var ContactInput = function ContactInput(_ref) {
  var chosenExtensions = _ref.chosenExtensions,
    setChosenExtensions = _ref.setChosenExtensions;
  var _useAppContext2 = useAppContext(),
    i18next = _useAppContext2.i18next;
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    searchText = _useState4[0],
    setSearchText = _useState4[1];
  var _useState5 = useState({
      filter: []
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    selectorState = _useState6[0],
    setSelectorState = _useState6[1];
  var inputRef = useAutoFocus();
  var addExtension = function addExtension() {
    if (selectorState.proposedExtension) {
      var _inputRef$current;
      setChosenExtensions([].concat(_toConsumableArray(chosenExtensions), [selectorState.proposedExtension]));
      setSearchText('');
      setSelectorState({
        filter: []
      });
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: "flex flex-wrap gap-x-3 gap-y-2",
      children: [chosenExtensions.map(function (extension, index) {
        return /*#__PURE__*/_jsx(ExtensionPill, {
          extension: extension,
          onRemove: function onRemove() {
            var _inputRef$current2;
            setChosenExtensions([].concat(_toConsumableArray(chosenExtensions.slice(0, index)), _toConsumableArray(chosenExtensions.slice(index + 1))));
            (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
          }
        }, extension.get('key'));
      }), /*#__PURE__*/_jsx("input", {
        className: "min-w-4 flex-1 border-0 bg-transparent p-0 text-base/5 placeholder:text-gray-500 focus:ring-0 focus:outline-hidden",
        ref: inputRef,
        type: "search",
        placeholder: chosenExtensions.length === 0 ? i18next.t('input.add_members') : undefined,
        value: searchText,
        onChange: function onChange(e) {
          var text = e.target.value;
          setSearchText(text);
          setSelectorState(_objectSpread(_objectSpread({}, selectorState), {}, {
            filter: text.split(/\s+/).filter(function (w) {
              return w !== '';
            }).map(function (w) {
              return w.toLowerCase();
            })
          }));
        },
        onKeyDown: function onKeyDown(e) {
          switch (e.key) {
            case 'ArrowDown':
              setSelectorState(_objectSpread(_objectSpread({}, selectorState), {}, {
                extensionOffset: selectorState.extensionOffset + 1
              }));
              e.preventDefault();
              break;
            case 'ArrowUp':
              setSelectorState(_objectSpread(_objectSpread({}, selectorState), {}, {
                extensionOffset: selectorState.extensionOffset - 1
              }));
              e.preventDefault();
              break;
            case 'Backspace':
              if (e.target.value === '' && chosenExtensions.length > 0) {
                setChosenExtensions(chosenExtensions.slice(0, chosenExtensions.length - 1));
              }
              break;
            case 'Enter':
              addExtension();
              e.preventDefault();
              break;
          }
        }
      })]
    }), /*#__PURE__*/_jsx(ContactSelectionPopover, {
      state: selectorState,
      updateState: setSelectorState,
      exclude: chosenExtensions,
      onChoose: addExtension,
      ref: inputRef
    })]
  });
};
var ContactSelectionPopover = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var state = _ref2.state,
    exclude = _ref2.exclude,
    updateState = _ref2.updateState,
    onChoose = _ref2.onChoose;
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    popperElement = _useState8[0],
    setPopperElement = _useState8[1];
  var referenceElement = ref;
  var _usePopper = usePopper(referenceElement.current, popperElement, {
      placement: 'bottom-start',
      modifiers: [{
        name: 'flip',
        enabled: true,
        options: {
          flipVariations: true,
          allowedAutoPlacements: ['top', 'bottom']
        }
      }]
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes;
  var hasChatCapability = function hasChatCapability(e) {
    return getExtensionHasCapability(e, Capabilities.CHAT);
  };

  // Filter extensions that we should show.
  var allExtensions = useAppSelector(function (state) {
    var _state$extensions$get;
    var extensions = new Array();
    (_state$extensions$get = state.extensions.get('extensions')) === null || _state$extensions$get === void 0 || _state$extensions$get.forEach(function (e) {
      return extensions.push(e);
    });
    return extensions;
  });
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var filteredExtensions = useMemo(function () {
    return allExtensions.filter(function (e) {
      return e.get('key') !== ownExtensionKey && !exclude.some(function (existing) {
        return existing.get('key') === e.get('key');
      });
    }).filter(hasChatCapability).filter(function (e) {
      return filterByAllfileds(e, state.filter);
    }).sort(sortByName);
  }, [allExtensions, state.filter, exclude, ownExtensionKey]);

  // The displayed extensions (accounting for arrow key handling), as well as the current offset
  // of the extensions we show.
  var _useState9 = useState(),
    _useState0 = _slicedToArray(_useState9, 2),
    displayedExtensions = _useState0[0],
    setDisplayedExtensions = _useState0[1];
  var _useState1 = useState(0),
    _useState10 = _slicedToArray(_useState1, 2),
    offsetStart = _useState10[0],
    setOffsetStart = _useState10[1];
  var maxExtensions = 6;
  useEffect(function () {
    var _state$proposedExtens;
    // If no extensions to show, don't go any further, and clear state.
    if (state.filter.length === 0 || filteredExtensions.length === 0) {
      setDisplayedExtensions(undefined);
      setOffsetStart(0);
      updateState({
        filter: state.filter
      });
      return;
    }

    // Make sure extension offset is in range; reset to 0 if not (or if unset), and determine
    // correct extension candidate. Update state if needed.
    var newExtensionOffset = Math.min(Math.max(state.extensionOffset || 0, 0), filteredExtensions.length - 1);
    var newProposedExtension = filteredExtensions[newExtensionOffset];
    if (state.extensionOffset !== newExtensionOffset || ((_state$proposedExtens = state.proposedExtension) === null || _state$proposedExtens === void 0 ? void 0 : _state$proposedExtens.get('key')) !== newProposedExtension.get('key')) {
      updateState(_objectSpread(_objectSpread({}, state), {}, {
        extensionOffset: newExtensionOffset,
        proposedExtension: newProposedExtension
      }));
    }

    // Make sure the offset start doesn't result in us showing less than the
    // desired number of extensions, and that the extension we are currently on
    // is in the window.
    var newOffsetStart = offsetStart;
    if (filteredExtensions.length - offsetStart < maxExtensions) {
      newOffsetStart = 0;
    }
    if (newExtensionOffset < offsetStart) {
      // We're too far down the list; move up.
      newOffsetStart = newExtensionOffset;
    } else if (newExtensionOffset >= offsetStart + maxExtensions) {
      // We're too far up the list; move down.
      newOffsetStart = newExtensionOffset - maxExtensions + 1;
    }
    setDisplayedExtensions(filteredExtensions.slice(newOffsetStart, newOffsetStart + maxExtensions));
    if (newOffsetStart !== offsetStart) {
      setOffsetStart(newOffsetStart);
    }
  }, [state.filter, state.extensionOffset || -1, offsetStart]);
  var _useAppContext3 = useAppContext(),
    i18next = _useAppContext3.i18next;
  var statuses = useAppSelector(function (state) {
    return state.extensions.get('statuses');
  });
  return /*#__PURE__*/_jsx(Popover, {
    children: displayedExtensions && /*#__PURE__*/_jsx(PopoverPanel, _objectSpread(_objectSpread({
      "static": true,
      ref: setPopperElement,
      style: styles.popper
    }, attributes.popper), {}, {
      className: "z-30 w-80 truncate rounded-sm bg-white p-2 shadow-lg outline-hidden",
      children: /*#__PURE__*/_jsx("div", {
        className: "flex flex-col",
        children: displayedExtensions.map(function (extension, index) {
          var _state$proposedExtens2;
          var contact = extension.get('contact');
          var name = extractName(contact, i18next.t('Contact.Unknown'));
          var active = extension.get('key') === ((_state$proposedExtens2 = state.proposedExtension) === null || _state$proposedExtens2 === void 0 ? void 0 : _state$proposedExtens2.get('key'));
          return /*#__PURE__*/_jsxs("div", {
            className: twMerge('group flex h-12 w-full cursor-pointer items-center gap-x-4 rounded-sm p-2', active ? 'bg-gray-200' : ''),
            onMouseEnter: function onMouseEnter() {
              return updateState(_objectSpread(_objectSpread({}, state), {}, {
                extensionOffset: offsetStart + index
              }));
            },
            onWheel: function onWheel(e) {
              var notches = Math.ceil(Math.abs(e.deltaY / 120)) * (e.deltaY < 0 ? -1 : 1);
              var maxOffsetStart = Math.max(filteredExtensions.length - maxExtensions, 0);
              setOffsetStart(Math.min(Math.max(offsetStart + notches, 0), maxOffsetStart));
            },
            onClick: function onClick() {
              updateState(_objectSpread(_objectSpread({}, state), {}, {
                proposedExtension: extension
              }));
              onChoose();
            },
            children: [/*#__PURE__*/_jsx(ContactAvatar, {
              contact: contact,
              size: "medium",
              status: statuses.get(extension.get('key')) || 'unknown'
            }), /*#__PURE__*/_jsxs("div", {
              className: "flex flex-col truncate",
              children: [/*#__PURE__*/_jsx("p", {
                className: "w-full truncate overflow-hidden text-base/4 whitespace-nowrap",
                children: name
              }), active && /*#__PURE__*/_jsx("p", {
                className: "w-full truncate overflow-hidden text-sm/4 whitespace-nowrap text-gray-500",
                children: /*#__PURE__*/_jsx(ActiveProfileStatus, {
                  extensionKey: extension === null || extension === void 0 ? void 0 : extension.get('key')
                })
              })]
            })]
          }, extension.get('chatUserKey'));
        })
      })
    }))
  });
});
function ExtensionPill(_ref3) {
  var extension = _ref3.extension,
    onRemove = _ref3.onRemove;
  var _useAppContext4 = useAppContext(),
    i18next = _useAppContext4.i18next;
  var contact = extension.get('contact');
  return /*#__PURE__*/_jsxs("div", {
    className: "flex items-center gap-x-2 rounded-sm border border-gray-300 p-1 hover:bg-gray-200",
    children: [/*#__PURE__*/_jsx(ContactAvatar, {
      contact: contact,
      size: "small"
    }), /*#__PURE__*/_jsx("p", {
      className: "text-sm",
      children: extractAbbreviatedName(contact, i18next.t('Contact.Unknown'))
    }), /*#__PURE__*/_jsx("button", {
      "aria-label": i18next.t('remove'),
      onClick: onRemove,
      children: /*#__PURE__*/_jsx(Icon, {
        iconKey: "cancel",
        className: "text-gray-600"
      })
    })]
  });
}
var NewChatView = function NewChatView(_ref4) {
  var extensions = _ref4.extensions;
  // Find any existing chat session.
  var _useExtension = useExtension(),
    ownExtension = _useExtension.ownExtension;
  var existingSessions = useAppSelector(function (state) {
    return state.chats.get('sessions').filter(function (s) {
      return s.get('roomType') === 'session';
    });
  });
  var _useState11 = useState(),
    _useState12 = _slicedToArray(_useState11, 2),
    chatSessionKey = _useState12[0],
    setChatSessionKey = _useState12[1];
  useEffect(function () {
    var wantedMembers = [].concat(_toConsumableArray(extensions), [ownExtension]).map(function (e) {
      return e.get('chatUserKey');
    });
    var foundSession = existingSessions === null || existingSessions === void 0 ? void 0 : existingSessions.find(function (existing) {
      var gotMembers = existing.get('members');
      return wantedMembers.length === gotMembers.size && wantedMembers.every(function (wanted) {
        return gotMembers.find(function (m) {
          return m.get('key') === wanted;
        });
      });
    });
    setChatSessionKey(foundSession === null || foundSession === void 0 ? void 0 : foundSession.get('key'));
  }, [existingSessions, extensions, ownExtension]);

  // Show message list and input, redirecting on message send, for existing chats, or an input
  // that will create a chat session for a new chat.
  var history = useHistory();
  var dispatch = useAppDispatch();
  var clientKey = useAppSelector(function (state) {
    return state.chats.getIn(['client', 'key']);
  });
  return chatSessionKey ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MessageList, {
      chatSessionKey: chatSessionKey
    }), /*#__PURE__*/_jsx("footer", {
      className: "flex flex-col items-center bg-white px-4 py-3",
      children: /*#__PURE__*/_jsx(MessageInput, {
        chatSession: chatSessionKey,
        afterSend: function afterSend() {
          return history.push(generatePath(routes['messages.id'], {
            id: chatSessionKey
          }));
        },
        preventInitialFocus: true
      })
    })]
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "flex-1"
    }), /*#__PURE__*/_jsx("footer", {
      className: "flex flex-col items-center bg-white px-4 py-3",
      children: /*#__PURE__*/_jsx(MessageInput, {
        preventInitialFocus: true,
        onSend: function onSend(text, attachment, alsoAsSMS) {
          return dispatch(createAndShowChatSessionWithMessage({
            members: extensions.map(function (e) {
              return Map({
                key: e.get('chatUserKey')
              });
            })
          }, clientKey, text, attachment, function (newSession) {
            return history.push(generatePath(routes['messages.id'], {
              id: newSession
            }));
          }, alsoAsSMS));
        },
        totalNewChatMembers: extensions.filter(function (extension) {
          return extension.get('key');
        }).length
      })
    })]
  });
};
var sortByName = function sortByName(a, b) {
  var aName = extractName(a.get('contact'));
  var bName = extractName(b.get('contact'));
  return aName < bName ? -1 : 1;
};
export default NewChatPage;